import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEO from "@/components/feature/SEO";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { getComicById, getHotComics } from "@/mocks/comics";
import { getArticleByComicId } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);
  const comic = getComicById(comicId);
  const article = getArticleByComicId(comicId);
  const related = getHotComics().filter((c) => c.id !== comicId).slice(0, 4);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-3xl" />
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的漫画资源不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catInfo = categories.find((c) => c.slug === comic.categorySlug);

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${comic.title} - 51漫画免费下载 | 51漫画${comic.category}漫画`}
        description={`51漫画提供${comic.title}免费下载，${comic.description.slice(0, 120)}。支持全集离线阅读与手机客户端下载。`}
        keywords={`${comic.title}下载,51漫画${comic.title},51漫画免费下载,51漫画${comic.category}下载,离线漫画,免费漫画下载`}
        canonicalUrl={`https://www.51manhua.com/article/${comic.id}`}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 51漫画`,
          description: comic.description,
          author: { "@type": "Person", name: comic.author },
          datePublished: comic.updateDate,
          publisher: {
            "@type": "Organization",
            name: "51漫画",
          },
          url: `https://www.51manhua.com/article/${comic.id}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.51manhua.com/" },
            { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://www.51manhua.com/category/rexue" },
            { "@type": "ListItem", position: 3, name: comic.category, item: `https://www.51manhua.com/category/${comic.categorySlug}` },
            { "@type": "ListItem", position: 4, name: comic.title },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: "漫画分类", href: "/category/rexue" },
                  { label: comic.category, href: `/category/${comic.categorySlug}` },
                  { label: comic.title },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <article>
                  <header className="mb-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Link
                        to={`/category/${comic.categorySlug}`}
                        className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap"
                      >
                        {comic.category}
                      </Link>
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                        {comic.status}
                      </span>
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line" />
                        {comic.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500" />
                        {comic.rating} 分
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-file-list-3-line" />
                        共 {comic.chapters} 话
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        更新于 {comic.updateDate}
                      </span>
                    </div>
                  </header>

                  <div className="mb-8">
                    <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-background-200 mb-6">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 51漫画免费下载`}
                        title={`${comic.title} - 51漫画免费下载`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="prose max-w-none">
                    <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">作品简介</h2>
                    <p className="text-foreground-700 leading-relaxed mb-6">
                      {comic.description}
                    </p>

                    {article && (
                      <div className="mt-6">
                        {article.content.split("\n\n").map((paragraph, i) => {
                          if (paragraph.startsWith("## ")) {
                            return (
                              <h3 key={i} className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">
                                {paragraph.replace("## ", "")}
                              </h3>
                            );
                          }
                          if (paragraph.startsWith("- ")) {
                            return (
                              <li key={i} className="text-foreground-700 ml-4 mb-1">
                                {paragraph.replace("- ", "")}
                              </li>
                            );
                          }
                          if (paragraph.trim()) {
                            return (
                              <p key={i} className="text-foreground-700 leading-relaxed mb-4">
                                {paragraph}
                              </p>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </article>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="p-5 rounded-xl bg-background-100/70 border border-background-200/70">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">漫画信息</h3>
                    <dl className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <dt className="text-foreground-500">作者</dt>
                        <dd className="text-foreground-800 font-medium">{comic.author}</dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-foreground-500">分类</dt>
                        <dd className="text-foreground-800 font-medium">{comic.category}</dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-foreground-500">状态</dt>
                        <dd className="text-foreground-800 font-medium">{comic.status}</dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-foreground-500">总话数</dt>
                        <dd className="text-foreground-800 font-medium">{comic.chapters}话</dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-foreground-500">评分</dt>
                        <dd className="text-accent-600 font-bold">{comic.rating}</dd>
                      </div>
                    </dl>

                    <div className="mt-5 pt-5 border-t border-background-200/70 space-y-2.5">
                      <a
                        href="#"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-download-2-line" />
                        免费下载全集
                      </a>
                      <a
                        href="#download"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-smartphone-line" />
                        下载APP阅读
                      </a>
                    </div>
                  </div>

                  {related.length > 0 && (
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">推荐漫画</h3>
                      <div className="space-y-3" data-product-shop>
                        {related.map((r) => (
                          <Link
                            key={r.id}
                            to={`/article/${r.id}`}
                            className="flex gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/50 transition-all group"
                          >
                            <div className="relative w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                              <img
                                src={r.cover}
                                alt={r.title}
                                title={`${r.title} - 51漫画免费下载`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                                {r.title}
                              </h4>
                              <p className="text-xs text-foreground-500 mt-0.5">{r.author}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <i className="ri-star-fill text-accent-500 text-[10px]" />
                                <span className="text-xs text-foreground-500">{r.rating}</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between">
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  返回{comic.category}漫画列表
                </Link>
                <Link
                  to="/"
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-4-line" />
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}